import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Check, EyeOff, Loader2 } from 'lucide-react';

import { useLang, useT } from '@/i18n';
import { cn } from '@/lib/utils';
import {
  householdApi,
  householdErrorOf,
  type HouseholdCandidate,
  type HouseholdCandidatesResponse,
  type HouseholdMatchInput,
  type HouseholdMatchResponse,
} from './household.api';

/**
 * TANDA 2 · T2-B2 · #158 / #151b — el recuadro «¿Es alguna de estas personas?».
 *
 * Edd (23-oct, selector, sobre el boceto c2 del artifact JpREvkywtECwAopcS1MUbV)
 * eligió un RECUADRO dentro del paso de hogar, no una ventana encima: por eso
 * este componente, aunque se llama «Modal» en el reparto de archivos (§8 del
 * diseño), se pinta en línea, dentro de la fila del familiar.
 *
 * Desvío ya aceptado por el coordinador: la API trabaja por `hmId`, así que el
 * familiar se captura primero y el recuadro aparece después (no dentro de
 * «Agregar miembro» antes de capturar).
 *
 * Qué se muestra de cada candidato (diseño §3.4, lo mínimo): nombre de pila e
 * inicial del apellido, el año de nacimiento (la fecha completa SOLO si
 * coincide con la que dijo el familiar: no revela nada nuevo), el estado de su
 * cola SIN el nombre de la otra agente, y los últimos 4 del Medicaid ID SOLO
 * cuando el candidato salió por el CIN que el agente acaba de capturar (el
 * boceto aprobado dice «ni Medicaid»: con el CIN tecleado por el agente, los 4
 * últimos tampoco revelan nada nuevo). Ni teléfonos, ni dirección.
 *
 * El servidor decide todo y la web solo lo refleja:
 *   - inelegible / fuera del Bronx → botón apagado («declínalo con el motivo»);
 *     el «no llamar» NO bloquea (Edd, 23-oct: «se puede screenear igual»);
 *   - ni la fecha ni el nombre coinciden → botón apagado (el servidor responde
 *     409 HH_MATCH_WEAK siempre);
 *   - coincide solo uno → el servidor pide confirmación (409
 *     HH_MATCH_CONFIRM_REQUIRED) y se reenvía con `confirmMismatch: true`;
 *   - screeneado hace < 365 días → el servidor guarda `already_screened` y el
 *     familiar se cierra sin screening (Edd, 23-oct).
 */

export const candidatesQueryKey = (parentScreeningId: string, hmId: string) =>
  ['screening', parentScreeningId, 'household-candidates', hmId] as const;

/** 'YYYY-MM-DD' → 'MM/DD/YYYY' sin pasar por Date (sin corrimiento de zona). */
export function fmtDob(ymd: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(ymd ?? ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}

/** ISO → «12 de septiembre de 2026» / «September 12, 2026», en hora de Nueva
 *  York (la misma forma que la franja de T2-A en la pre-llamada). */
export function fmtScreenedDate(iso: string | null | undefined, lang: 'es' | 'en'): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(lang === 'es' ? 'es' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'America/New_York',
  });
}

/** ¿Hay respaldo para «es esta persona»? (espejo de matchStrength del servidor). */
export function isWeakCandidate(c: Pick<HouseholdCandidate, 'dobMatch' | 'firstNameMatch'>): boolean {
  return !c.dobMatch && !c.firstNameMatch;
}

/** Lo que impide apretar «Es esta persona» desde la web (el servidor igual revalida). */
export function candidateDisabledReason(
  c: HouseholdCandidate,
): 'blocked' | 'linked' | 'weak' | null {
  if (c.blocked) return 'blocked';
  // `linked` solo rebota si NO va a quedar como «ya screeneado» (servidor:
  // el chequeo de vínculo está en la rama same_person).
  if (c.linked && !c.screenedAt) return 'linked';
  if (isWeakCandidate(c)) return 'weak';
  return null;
}

interface Props {
  parentScreeningId: string;
  hmId: string;
  /** Se cierra sin decidir: solo cuando no hay candidatos o la búsqueda falló. */
  onClose: () => void;
  /** La decisión quedó guardada en el servidor. `chosen` = el candidato elegido. */
  onDecided: (r: HouseholdMatchResponse, chosen: HouseholdCandidate | null) => void;
}

export function HouseholdMatchModal({ parentScreeningId, hmId, onClose, onDecided }: Props): JSX.Element {
  const t = useT();
  const lang = useLang();
  const titleId = useId();
  const boxRef = useRef<HTMLElement>(null);

  const q = useQuery<HouseholdCandidatesResponse>({
    queryKey: candidatesQueryKey(parentScreeningId, hmId),
    queryFn: () => householdApi.candidates(parentScreeningId, hmId),
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const [confirmFor, setConfirmFor] = useState<
    { clientId: string; dobMatch: boolean; firstNameMatch: boolean } | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  // Que el recuadro se vea al aparecer (el agente está mirando el formulario).
  useEffect(() => {
    const el = boxRef.current;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  const candidates = q.data?.candidates ?? [];
  const viaCin = q.data?.viaCin === true;

  const matchMut = useMutation({
    mutationFn: (input: HouseholdMatchInput) => householdApi.match(parentScreeningId, hmId, input),
    onSuccess: (r, input) => {
      setConfirmFor(null);
      setError(null);
      const chosen = input.clientId ? candidates.find((c) => c.clientId === input.clientId) ?? null : null;
      onDecided(r, chosen);
    },
    onError: (err, input) => {
      const info = householdErrorOf(err);
      if (info?.code === 'HH_MATCH_CONFIRM_REQUIRED' && input.clientId) {
        setError(null);
        setConfirmFor({
          clientId: input.clientId,
          dobMatch: info.extra.dobMatch === true,
          firstNameMatch: info.extra.firstNameMatch === true,
        });
        return;
      }
      setConfirmFor(null);
      setError(info?.message || (err as Error)?.message || String(err));
    },
  });

  const choose = (c: HouseholdCandidate, confirmMismatch = false): void => {
    setError(null);
    matchMut.mutate({
      decision: 'same_person',
      clientId: c.clientId,
      candidatesShown: candidates.length,
      ...(confirmMismatch ? { confirmMismatch: true } : {}),
    });
  };

  const none = (): void => {
    setError(null);
    setConfirmFor(null);
    matchMut.mutate({ decision: 'not_in_list', candidatesShown: candidates.length });
  };

  const attemptsLabel = (n: number): string =>
    n <= 0 ? t('t2b2.intentos.0') : n === 1 ? t('t2b2.intentos.1') : t('t2b2.intentos.n', { n });

  const chosenId =
    q.data && (q.data.matchDecision === 'same_person' || q.data.matchDecision === 'already_screened')
      ? q.data.matchedClientId
      : null;

  let body: JSX.Element;
  if (q.isLoading) {
    body = (
      <p className="flex items-center gap-2 text-sm text-nav-slate" data-testid="t2b2-buscando">
        <Loader2 className="h-4 w-4 animate-spin text-nav-teal" aria-hidden />
        {t('t2b2.box.buscando')}
      </p>
    );
  } else if (q.isError) {
    body = (
      <p className="text-sm text-nav-slate" data-testid="t2b2-error-carga">
        {t('t2b2.box.errorCarga')}
      </p>
    );
  } else if (candidates.length === 0) {
    body = (
      <p className="text-sm text-nav-slate" data-testid="t2b2-nadie">
        {t('t2b2.box.nadie')}
      </p>
    );
  } else {
    body = (
      <ul className="space-y-2" data-testid="t2b2-candidatos">
        {candidates.map((c) => {
          const name = c.lastInitial ? `${c.firstName} ${c.lastInitial}.` : c.firstName;
          const disabledWhy = candidateDisabledReason(c);
          const isChosen = chosenId === c.clientId;
          const meta: string[] = [];
          if (c.dobMatch && c.dob) {
            meta.push(`${t('t2b2.cand.nacioFecha', { date: fmtDob(c.dob) })} (${t('t2b2.cand.coincide')})`);
          } else if (c.dobYear) {
            meta.push(t('t2b2.cand.nacioAnio', { year: c.dobYear }));
          }
          meta.push(t(`t2b2.cola.${c.queueState}` as never));
          meta.push(attemptsLabel(c.attemptCount));
          if (viaCin && c.cinLast4) meta.push(t('t2b2.cand.medicaid', { last4: c.cinLast4 }));
          const confirming = confirmFor?.clientId === c.clientId;
          return (
            <li
              key={c.clientId}
              data-testid="t2b2-candidato"
              data-client-id={c.clientId}
              className={cn(
                'rounded-lg border bg-white px-3 py-2.5',
                isChosen ? 'border-nav-teal ring-1 ring-nav-teal/40' : 'border-nav-card',
              )}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-nav-navy">{name}</p>
                  <p className="text-xs text-nav-slate" data-testid="t2b2-candidato-meta">
                    {meta.join(' · ')}
                  </p>
                  {c.screenedAt && (
                    <p className="text-xs font-semibold text-nav-amberText mt-0.5" data-testid="t2b2-ya-screeneada">
                      {t('t2b2.cand.yaScreeneada', { date: fmtScreenedDate(c.screenedAt, lang) })}
                    </p>
                  )}
                  {disabledWhy === 'blocked' && c.blocked && (
                    <p className="text-xs text-nav-red mt-0.5" data-testid="t2b2-bloqueada">
                      <span className="font-semibold">{t(`t2b2.bloq.${c.blocked}` as never)}</span>
                      {' · '}
                      {t('t2b2.bloq.ayuda')}
                    </p>
                  )}
                  {disabledWhy === 'linked' && (
                    <p className="text-xs text-nav-slate mt-0.5" data-testid="t2b2-vinculada">
                      {t('t2b2.cand.linked')}
                    </p>
                  )}
                  {disabledWhy === 'weak' && (
                    <p className="text-xs text-nav-slate mt-0.5" data-testid="t2b2-debil">
                      {t('t2b2.cand.debil')}
                    </p>
                  )}
                </div>
                {isChosen ? (
                  <span
                    className="inline-flex items-center gap-1 self-start sm:self-center rounded-full border border-nav-teal bg-nav-tealTint px-2.5 py-1 text-xs font-semibold text-nav-tealDark"
                    data-testid="t2b2-elegida"
                  >
                    <Check className="h-3.5 w-3.5" aria-hidden />
                    {t('t2b2.box.elegida')}
                  </span>
                ) : (
                  <button
                    type="button"
                    data-testid="t2b2-es-esta"
                    disabled={disabledWhy !== null || matchMut.isPending || confirming}
                    onClick={() => choose(c)}
                    className={cn(
                      'shrink-0 self-start sm:self-center inline-flex items-center justify-center gap-1.5',
                      'h-9 rounded-lg border-[1.5px] border-nav-line bg-white px-4 text-sm font-semibold text-nav-navy',
                      'hover:border-nav-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
                      'disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:border-nav-line',
                    )}
                  >
                    {matchMut.isPending && matchMut.variables?.clientId === c.clientId && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    )}
                    {t('t2b2.box.esEsta')}
                  </button>
                )}
              </div>
              {confirming && confirmFor && (
                <div
                  className="mt-2 rounded-md border border-nav-amber bg-nav-amberTint px-3 py-2"
                  role="alertdialog"
                  aria-label={t('t2b2.box.esEsta')}
                  data-testid="t2b2-confirmar"
                >
                  <p className="text-xs font-semibold text-nav-amberText">
                    {confirmFor.dobMatch && !confirmFor.firstNameMatch
                      ? t('t2b2.confirm.soloFecha')
                      : t('t2b2.confirm.soloNombre')}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid="t2b2-confirmar-si"
                      disabled={matchMut.isPending}
                      onClick={() => choose(c, true)}
                      className="h-8 rounded-lg bg-nav-navy px-3 text-xs font-semibold text-white hover:bg-nav-navyHover disabled:opacity-50"
                    >
                      {t('t2b2.confirm.si')}
                    </button>
                    <button
                      type="button"
                      data-testid="t2b2-confirmar-no"
                      disabled={matchMut.isPending}
                      onClick={() => setConfirmFor(null)}
                      className="h-8 rounded-lg border border-nav-line bg-white px-3 text-xs font-semibold text-nav-navy"
                    >
                      {t('t2b2.confirm.no')}
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  const showNone = !q.isLoading && !q.isError && candidates.length > 0;

  return (
    <section
      ref={boxRef}
      role="region"
      aria-labelledby={titleId}
      data-testid="t2b2-match-box"
      data-hm-id={hmId}
      className="rounded-xl border-[1.5px] border-nav-amber bg-nav-amberTint p-4 space-y-3"
    >
      <div>
        <h3 id={titleId} className="font-head text-base font-bold text-nav-navy">
          {t('t2b2.box.titulo')}
        </h3>
        <p className="text-xs text-nav-amberText mt-0.5">
          {viaCin ? t('t2b2.box.subCin') : t('t2b2.box.sub')}
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-nav-navy" data-testid="t2b2-no-leer">
          <EyeOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {t('t2b2.box.noLeer')}
        </p>
      </div>

      {body}

      {error && (
        <div
          role="alert"
          data-testid="t2b2-error"
          className="flex items-start gap-1.5 rounded-md border border-nav-red bg-nav-redTint px-3 py-2 text-xs text-nav-red"
        >
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
          <span>
            <span className="font-semibold">{t('t2b2.error.titulo')}.</span> {error}
          </span>
        </div>
      )}

      <div className="flex justify-end">
        {showNone ? (
          <button
            type="button"
            data-testid="t2b2-ninguna"
            disabled={matchMut.isPending}
            onClick={none}
            className="text-sm font-bold text-nav-tealDark hover:underline disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal rounded"
          >
            {matchMut.isPending && matchMut.variables?.decision === 'not_in_list' && (
              <Loader2 className="mr-1 inline h-3.5 w-3.5 animate-spin" aria-hidden />
            )}
            {t('t2b2.box.ninguna')}
          </button>
        ) : q.isLoading ? null : (
          <button
            type="button"
            data-testid="t2b2-cerrar"
            onClick={onClose}
            className="text-sm font-bold text-nav-tealDark hover:underline"
          >
            {t('t2b2.box.cerrar')}
          </button>
        )}
      </div>
    </section>
  );
}
